import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, HomeSolar } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const solarSchema = z.object({
  panel_count:          z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().min(1).max(10000).nullable()),
  panel_brand:          z.string().max(100).trim().optional(),
  panel_wattage:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().min(1).max(10000).nullable()),
  system_size_kw:       z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().min(0).max(10000).nullable()),
  inverter_brand:       z.string().max(100).trim().optional(),
  inverter_type:        z.enum(['string','micro','power_optimizer','hybrid','']).optional(),
  battery_storage:      z.boolean(),
  battery_brand:        z.string().max(100).trim().optional(),
  battery_capacity_kwh: z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().min(0).nullable()),
  install_year:         z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().min(1990).max(CURRENT_YEAR + 1).nullable()),
  installer_name:       z.string().max(200).trim().optional(),
  warranty_panel_years:    z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().min(0).max(50).nullable()),
  warranty_inverter_years: z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().min(0).max(50).nullable()),
  monitoring_app:       z.string().max(100).trim().optional(),
  net_metering:         z.boolean(),
});

type SolarForm = z.infer<typeof solarSchema>;

function WarrantyBar({ label, installYear, warrantyYears }: { label: string; installYear: number | null; warrantyYears: number | null }) {
  if (!installYear || !warrantyYears) return null;
  const elapsed = CURRENT_YEAR - installYear;
  const remaining = Math.max(0, warrantyYears - elapsed);
  const pct = Math.min(100, (remaining / warrantyYears) * 100);
  const color = remaining === 0 ? 'bg-red-500' : remaining <= 3 ? 'bg-amber-500' : 'bg-green-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className={remaining === 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
          {remaining > 0 ? `${remaining} yr${remaining === 1 ? '' : 's'} left` : 'Expired'}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-400">{warrantyYears}-year warranty · installed {installYear}</p>
    </div>
  );
}

function StatBox({ label, value, unit }: { label: string; value: string | null; unit?: string | undefined }) {
  return (
    <div className="bg-white rounded-xl p-3 text-center">
      <p className="text-xl font-bold text-gray-900">{value ?? '—'}{unit && value ? <span className="text-sm font-medium text-gray-500 ml-0.5">{unit}</span> : ''}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | boolean }) {
  if (value === null || value === undefined) return null;
  const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).replace(/_/g, ' ');
  return (
    <div className="flex justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 capitalize">{display}</span>
    </div>
  );
}

export function SolarDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const { data: solar, isLoading } = useQuery({
    queryKey: ['solar', home?.id ?? ''],
    queryFn: async () => {
      try {
        return await api.get<HomeSolar>(`/homes/${home!.id}/solar`);
      } catch {
        return null;
      }
    },
    enabled: !!home,
  });

  const upsertMutation = useMutation({
    mutationFn: (data: SolarForm) => {
      const payload = {
        panel_count:    data.panel_count ?? 1,
        panel_brand:    data.panel_brand   || undefined,
        panel_wattage:  data.panel_wattage ?? undefined,
        system_size_kw: data.system_size_kw ?? undefined,
        inverter_brand: data.inverter_brand || undefined,
        inverter_type:  (data.inverter_type || undefined) as HomeSolar['inverter_type'] | undefined,
        battery_storage: data.battery_storage,
        battery_brand:   data.battery_brand || undefined,
        battery_capacity_kwh: data.battery_capacity_kwh ?? undefined,
        install_year:    data.install_year ?? undefined,
        installer_name:  data.installer_name || undefined,
        warranty_panel_years:    data.warranty_panel_years ?? undefined,
        warranty_inverter_years: data.warranty_inverter_years ?? undefined,
        monitoring_app: data.monitoring_app || undefined,
        net_metering:   data.net_metering,
      };
      return solar
        ? api.put<HomeSolar>(`/homes/${home!.id}/solar`, payload)
        : api.post<HomeSolar>(`/homes/${home!.id}/solar`, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['solar'] });
      void queryClient.invalidateQueries({ queryKey: ['homes'] });
      setShowForm(false);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-5 px-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/app/profile')} className="flex items-center gap-1 text-white/80 text-sm" style={{ minHeight: 44 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Profile
          </button>
          <h1 className="text-white font-bold text-lg">☀️ Solar System</h1>
          {solar && (
            <button onClick={() => setShowForm(true)} className="text-white/90 text-sm font-semibold bg-white/20 px-3 py-1.5 rounded-full" style={{ minHeight: 44 }}>
              Edit
            </button>
          )}
          {!solar && <div style={{ width: 60 }} />}
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {!solar ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <p className="text-5xl mb-3">☀️</p>
            <p className="font-semibold text-gray-800 mb-2">No solar system registered</p>
            <p className="text-sm text-gray-500 mb-5">Add your solar system to track warranties and generate maintenance tasks.</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm"
              style={{ minHeight: 44 }}
            >
              Add solar system
            </button>
          </div>
        ) : (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2">
              <StatBox label="Panels" value={solar.panel_count !== null ? String(solar.panel_count) : null} />
              <StatBox label="System size" value={solar.system_size_kw !== null ? String(solar.system_size_kw) : null} unit="kW" />
              <StatBox label="Panel wattage" value={solar.panel_wattage !== null ? String(solar.panel_wattage) : null} unit="W" />
              <StatBox label="Battery" value={solar.battery_storage ? (solar.battery_capacity_kwh !== null ? String(solar.battery_capacity_kwh) : 'Yes') : 'None'} unit={solar.battery_storage && solar.battery_capacity_kwh ? 'kWh' : undefined} />
            </div>

            {/* Details card */}
            <div className="bg-white rounded-2xl shadow-sm px-4">
              <DetailRow label="Panel brand"    value={solar.panel_brand} />
              <DetailRow label="Inverter brand" value={solar.inverter_brand} />
              <DetailRow label="Inverter type"  value={solar.inverter_type} />
              <DetailRow label="Installer"      value={solar.installer_name} />
              <DetailRow label="Install year"   value={solar.install_year !== null ? String(solar.install_year) : null} />
              <DetailRow label="Monitoring app" value={solar.monitoring_app} />
              <DetailRow label="Net metering"   value={solar.net_metering} />
              {solar.battery_storage && (
                <DetailRow label="Battery brand" value={solar.battery_brand} />
              )}
            </div>

            {/* Warranty cards */}
            {(solar.warranty_panel_years ?? solar.warranty_inverter_years) ? (
              <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
                <p className="text-sm font-bold text-gray-900">Warranties</p>
                <WarrantyBar label="Panels" installYear={solar.install_year} warrantyYears={solar.warranty_panel_years} />
                <WarrantyBar label="Inverter" installYear={solar.install_year} warrantyYears={solar.warranty_inverter_years} />
                {solar.battery_storage && (
                  <WarrantyBar label="Battery" installYear={solar.install_year} warrantyYears={10} />
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      {showForm && home && (
        <SolarFormModal
          initial={solar ?? null}
          onClose={() => setShowForm(false)}
          onSubmit={(data) => upsertMutation.mutate(data)}
          isPending={upsertMutation.isPending}
          error={upsertMutation.error instanceof Error ? upsertMutation.error.message : null}
        />
      )}

      <NavBar />
    </div>
  );
}

function SolarFormModal({
  initial, onClose, onSubmit, isPending, error,
}: {
  initial: HomeSolar | null;
  onClose: () => void;
  onSubmit: (data: SolarForm) => void;
  isPending: boolean;
  error: string | null;
}) {
  const { register, handleSubmit, watch } = useForm<SolarForm>({
    resolver: zodResolver(solarSchema),
    defaultValues: {
      panel_count:          initial?.panel_count ?? null,
      panel_brand:          initial?.panel_brand ?? '',
      panel_wattage:        initial?.panel_wattage ?? null,
      system_size_kw:       initial?.system_size_kw ?? null,
      inverter_brand:       initial?.inverter_brand ?? '',
      inverter_type:        (initial?.inverter_type ?? '') as SolarForm['inverter_type'],
      battery_storage:      initial?.battery_storage ?? false,
      battery_brand:        initial?.battery_brand ?? '',
      battery_capacity_kwh: initial?.battery_capacity_kwh ?? null,
      install_year:         initial?.install_year ?? null,
      installer_name:       initial?.installer_name ?? '',
      warranty_panel_years:    initial?.warranty_panel_years ?? null,
      warranty_inverter_years: initial?.warranty_inverter_years ?? null,
      monitoring_app:       initial?.monitoring_app ?? '',
      net_metering:         initial?.net_metering ?? true,
    },
  });

  const hasBattery = watch('battery_storage');

  const inp = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end" onClick={onClose}>
      <div className="w-full bg-white rounded-t-3xl p-5 max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">{initial ? 'Edit' : 'Add'} Solar System</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl p-1" style={{ minHeight: 44 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Panel count *</label>
              <input {...register('panel_count')} type="number" placeholder="e.g. 24" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">System size (kW)</label>
              <input {...register('system_size_kw')} type="number" step="0.1" placeholder="e.g. 8.4" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Panel wattage (W)</label>
              <input {...register('panel_wattage')} type="number" placeholder="e.g. 350" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Install year</label>
              <input {...register('install_year')} type="number" placeholder="e.g. 2022" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Panel brand</label>
              <input {...register('panel_brand')} placeholder="e.g. LG" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Inverter brand</label>
              <input {...register('inverter_brand')} placeholder="e.g. Enphase" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Inverter type</label>
              <select {...register('inverter_type')} className={inp}>
                <option value="">Select…</option>
                <option value="string">String</option>
                <option value="micro">Micro</option>
                <option value="power_optimizer">Power optimizer</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Installer</label>
              <input {...register('installer_name')} placeholder="Company name" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Panel warranty (yrs)</label>
              <input {...register('warranty_panel_years')} type="number" placeholder="e.g. 25" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Inverter warranty (yrs)</label>
              <input {...register('warranty_inverter_years')} type="number" placeholder="e.g. 15" className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Monitoring app</label>
              <input {...register('monitoring_app')} placeholder="e.g. Tesla" className={inp} />
            </div>
          </div>

          <div className="flex gap-3">
            <label className="flex items-center gap-2 flex-1 cursor-pointer">
              <input {...register('battery_storage')} type="checkbox" className="w-4 h-4 rounded" />
              <span className="text-sm text-gray-700">Battery storage installed</span>
            </label>
            <label className="flex items-center gap-2 flex-1 cursor-pointer">
              <input {...register('net_metering')} type="checkbox" className="w-4 h-4 rounded" />
              <span className="text-sm text-gray-700">Net metering active</span>
            </label>
          </div>

          {hasBattery && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Battery brand</label>
                <input {...register('battery_brand')} placeholder="e.g. Tesla" className={inp} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Capacity (kWh)</label>
                <input {...register('battery_capacity_kwh')} type="number" step="0.1" placeholder="e.g. 13.5" className={inp} />
              </div>
            </div>
          )}

          {error && <div className="bg-red-50 rounded-xl p-3 text-red-600 text-sm">{error}</div>}

          <button type="submit" disabled={isPending} className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-60">
            {isPending ? 'Saving…' : 'Save solar system'}
          </button>
        </form>
      </div>
    </div>
  );
}
